/* eslint-disable prefer-destructuring */
import { Session, SessionObj } from '../stores/types';
import { formatIso, getLastWeek } from '../utils/Date.Util';
import { getStartedStatus } from '../utils/session.util';
import { API_KEYS, Api } from './base.api';
import { SCOPE_TYPE, providesListTag } from './utils';

export interface SessionOptions {
  locationId?: string;
  chargerIds?: string[];
  dateRange?: Date[];
  vehicleId?: string;
  filter_hasCompletedTx?: boolean;
  sort?: string;
  scope: SCOPE_TYPE;
  companyId?: string;
}

const formatSessionParams = (params: any) => {
  const result: Record<string, any> = {};
  Object.entries(params)?.forEach(([key, value]: [string, any]) => {
    switch (key) {
      case 'locationId':
        if (value) {
          result['filter_eq[locationId]'] = value;
        }
        break;
      case 'chargerIds':
        if (value && value.length > 0) {
          result['filter_in[chargerId]'] = value.join(',');
        }
        break;
      case 'chargers':
        if (value && value.length > 0 && value.some((charger: any) => charger.selected)) {
          result['filter_in[chargerId]'] = value
            .filter((charger: any) => charger.selected)
            .map((charger: any) => charger.id)
            .join(',');
        }
        break;
      case 'dateRange': {
        let startDate;
        let endDate;
        if (value && value.length >= 2) {
          startDate = value[0];
          endDate = value[1];
        } else {
          const lastWeek = getLastWeek();
          startDate = lastWeek[0];
          endDate = lastWeek[1];
        }
        result['filter_ge[createTime]'] = formatIso(startDate);
        result['filter_le[createTime]'] = formatIso(endDate);
        break;
      }
      case 'vehicleId': {
        if (value) {
          result['filter_eq[vehicleId]'] = value;
        }
        break;
      }
      default:
        result[key] = value;
        break;
    }
  });
  return result;
};

export const sessionApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    fetchSessions: builder.query<SessionObj, SessionOptions>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.SESSIONS}`,
        params: formatSessionParams(params),
        method: 'GET',
      }),
      providesTags: (result, error, arg) => providesListTag(result?.entities, API_KEYS.SESSIONS),
      transformResponse: (response: SessionObj) => {
        return {
          totalCount: response.totalCount,
          entities: response.entities.map((session: Session) => {
            return {
              ...session,
              startedVia: getStartedStatus(session),
            };
          }),
        };
      },
    }),
    fetchSessionsByChargerId: builder.query<
      SessionObj,
      {
        chargerId: string;
        sort?: string;
        dateRange?: Date[];
        filter_hasCompletedTx?: boolean;
      }
    >({
      query: ({ chargerId, ...rest }) => ({
        url: `/internal/core/v2/chargers/${chargerId}/sessions`,
        method: 'GET',
        params: { ...formatSessionParams(rest), scope: SCOPE_TYPE.NONE },
        needCompanyId: false,
      }),
      transformResponse: (response: SessionObj) => {
        return {
          totalCount: response.totalCount,
          entities: response.entities.map((session: Session) => {
            return {
              ...session,
              startedVia: getStartedStatus(session),
            };
          }),
        };
      },
    }),
    fetchSessionById: builder.query<Session, string>({
      query: (sessionId) => ({
        url: `/internal/core/v2/${API_KEYS.SESSIONS}/${sessionId}`,
        method: 'GET',
        params: { scope: SCOPE_TYPE.ALL },
        needCompanyId: false,
      }),
      transformResponse: (response: SessionObj | Session) => {
        const entity =
          response && 'entities' in response ? response.entities?.[0] : (response as Session);
        return { ...entity, startedVia: getStartedStatus(entity) };
      },
    }),
    sessionStartCharging: builder.mutation<
      Session,
      { portId: string; scope: SCOPE_TYPE; companyId?: string }
    >({
      query: (payload) => ({
        url: `/internal/core/v2/${API_KEYS.SESSIONS}`,
        params: {
          scope: payload.scope,
          ...(payload.companyId && { companyId: payload.companyId }),
        },
        body: { portId: payload.portId },
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.CHARGERS, id: result?.chargerId },
        { type: API_KEYS.SESSIONS, id: 'LIST' },
      ],
    }),
    sessionStopCharging: builder.mutation<
      Session,
      { sessionId: string; scope: SCOPE_TYPE; companyId?: string }
    >({
      query: (payload) => ({
        url: `/internal/core/v2/${API_KEYS.SESSIONS}/${payload.sessionId}/stop`,
        params: {
          scope: payload.scope,
          ...(payload.companyId && { companyId: payload.companyId }),
        },
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => {
        return [
          { type: API_KEYS.CHARGERS, id: result?.chargerId },
          { type: API_KEYS.SESSIONS, id: 'LIST' },
        ];
      },
    }),
    sessionForceStopCharging: builder.mutation<
      Session,
      { sessionId: string; scope: SCOPE_TYPE; companyId?: string }
    >({
      query: (payload) => ({
        url: `/internal/core/v2/${API_KEYS.SESSIONS}/${payload.sessionId}/force-stop`,
        params: {
          scope: payload.scope,
          ...(payload.companyId && { companyId: payload.companyId }),
        },
        method: 'POST',
      }),
      invalidatesTags: (result) => {
        return [
          { type: API_KEYS.CHARGERS, id: result?.chargerId },
          { type: API_KEYS.SESSIONS, id: 'LIST' },
        ];
      },
    }),
  }),
});

export const {
  useFetchSessionsQuery,
  useLazyFetchSessionsQuery,
  useFetchSessionsByChargerIdQuery,
  useLazyFetchSessionByIdQuery,
  useSessionStartChargingMutation,
  useSessionStopChargingMutation,
  useSessionForceStopChargingMutation,
} = sessionApi;
